import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Tv, Loader2, ArrowLeft } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import TournamentsSection from "../components/TournamentsSection";
import PointsTableSection from "../components/PointsTableSection";
import LeadingStatsSection from "../components/LeadingStatsSection";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api from "../lib/api";

const BigGames = () => {
  const [games, setGames] = useState(null);

  useEffect(() => {
    api.get("/biggames").then((r) => setGames(r.data)).catch(() => setGames([]));
  }, []);

  return (
    <div className="min-h-screen bg-cream dark:bg-background text-foreground">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-12 md:py-16">
        <Reveal>
          <Eyebrow>Matchday hub</Eyebrow>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tighter">
            The big games
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            Everything happening on a pitch around Morbi — the fixtures worth planning around,
            upcoming tournaments, the points table, and who's topping the charts.
          </p>
        </Reveal>

        {games === null ? (
          <div className="mt-12 grid place-items-center py-16 text-muted-foreground" data-testid="biggames-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="mt-10 space-y-5">
            {games.length === 0 ? (
              <div className="rounded-3xl border-2 border-dashed border-foreground/15 p-10 text-center" data-testid="biggames-empty">
                <CalendarDays className="h-8 w-8 mx-auto text-muted-foreground" />
                <p className="mt-3 font-display font-bold text-lg">No big games listed yet</p>
                <p className="text-sm text-muted-foreground mt-1">I'll post the next one as soon as the fixtures drop.</p>
              </div>
            ) : (
              games.map((g, idx) => (
                <Reveal key={g.id} delay={0.04 * Math.min(idx, 4)}>
                  <article
                    data-testid="biggame-card"
                    className="rounded-3xl bg-card border border-foreground/10 p-6 md:p-7 flex flex-col sm:flex-row gap-5 sm:items-start shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="shrink-0 rounded-2xl bg-pitch text-cream dark:bg-turf dark:text-background px-5 py-4 text-center sm:min-w-[130px]">
                      <CalendarDays className="h-4 w-4 mx-auto opacity-80" />
                      <p className="mt-1.5 font-mono text-[11px] uppercase tracking-wide leading-relaxed">
                        {g.date_label || "Coming up"}
                      </p>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {g.competition && (
                          <span className="rounded-full bg-flame-soft dark:bg-flame/15 text-flame px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] font-semibold">
                            {g.competition}
                          </span>
                        )}
                        {g.watch_on && (
                          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                            <Tv className="h-3.5 w-3.5" /> {g.watch_on}
                          </span>
                        )}
                      </div>
                      <h2 className="mt-2.5 font-display text-xl sm:text-2xl font-bold tracking-tight leading-snug">
                        {g.title}
                      </h2>
                      {g.note && <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">{g.note}</p>}
                    </div>
                  </article>
                </Reveal>
              ))
            )}
          </div>
        )}

        <TournamentsSection />
        <PointsTableSection />
        <LeadingStatsSection />

        <div className="mt-16 text-center">
          <Link
            to="/"
            data-testid="biggames-back-home"
            className="inline-flex items-center gap-2 h-12 px-7 rounded-full border-2 border-pitch/25 dark:border-turf/40 font-semibold text-pitch dark:text-turf hover:border-pitch dark:hover:border-turf transition-all"
          >
            <ArrowLeft className="h-4 w-4" /> Back to the project
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BigGames;
